import { useCallback, useContext, useState } from 'react';
import type React from 'react';
import classNames from 'classnames';
import { useTypedDispatch } from '../../Store/connectors';
import { removeCartItemMessage, setCartItemMessage } from '../../Store/CartItem/action';
import { useTextFilter } from '../../../general/hooks/useTextFilter';
import SideBar from '../../../general/SideBar/SideBar';
import GiftCardEditor from './GiftCardEditor';
import Translation from '../../../general/Translation/Translation';
import { useWithImageUrl } from '../../../general/WebshopContext/WebshopContext';
import styles from './GiftCard.module.scss';
import SideBarHeader from '../../../general/SideBar/SideBarContent/SideBarHeader';
import SideBarBody from '../../../general/SideBar/SideBarContent/SideBarBody';
import Button from '../../../general/Button/Button';
import { TopLayerContext } from '../../../general/TopLayer/TopLayerContext';
import useTranslate from '../../../general/Translation/hooks/UseTranslate';

interface GiftCardProps {
    cartItemId: string;
    maxCharacters: number;
    text: string;
}

interface GiftCardFormProps {
    cartItemId: string;
    defaultValue: string;
    maxCharacters: number;
    onMessageChange: (message: string) => void;
}

const GiftCardForm: React.FC<GiftCardFormProps> = ({ cartItemId, defaultValue, maxCharacters, onMessageChange }) => {
    const { closeTopLayerElement } = useContext(TopLayerContext);
    const [message, setMessage] = useState<string>(defaultValue);
    const { filterBySuperNett } = useTextFilter();
    const [textFilterWarningShown, setTextFilterWarningShown] = useState<boolean>(false);

    const withImageUrl = useWithImageUrl();
    const dispatch = useTypedDispatch();

    const onChange = (enteredMessage: string) => {
        const filteredMessage = filterBySuperNett(enteredMessage);

        setMessage(filteredMessage);
        setTextFilterWarningShown(filteredMessage !== enteredMessage);
        onMessageChange(filteredMessage);
    };

    const clearGiftCard = () => onChange('');

    const saveMessage = useCallback(() => {
        closeTopLayerElement();
        if (message.length) {
            dispatch(setCartItemMessage({ id: cartItemId, message }));
        } else {
            dispatch(removeCartItemMessage({ id: cartItemId }));
        }
    }, [cartItemId, closeTopLayerElement, dispatch, message]);

    return (
        <div className={classNames(styles.GiftCard__content)}>
            <div className={styles.GiftCard__content__image}>
                <img src={withImageUrl('/upsell/giftcard-nw.png')} alt="GiftCard"/>
            </div>
            <div className={styles.GiftCard__content__message}>
                <textarea
                    className="form-input form-textarea dirty"
                    value={message}
                    maxLength={maxCharacters}
                    onChange={(e) => onChange(e.currentTarget.value)}
                />
                <div className={styles.GiftCard__content__message__info} data-clarity-mask="True">
                    <span
                        role={message.length >= maxCharacters ? 'alert' : 'status'}
                        className={classNames(styles.CharacterCounter, {
                            [styles.maxReached as string]: message.length >= maxCharacters,
                        })}>{message.length} / {maxCharacters}
                        {' '}
                        <Translation pageString="upsell" stringId="characters_left"/>
                    </span>
                    <Button className="button--link" onClick={clearGiftCard}>
                        <Translation pageString="upsell" stringId="delete_button_text"/>
                    </Button>
                </div>
                {textFilterWarningShown && <div className="form-error">
                    <span className="error"><Translation pageString="upsell" stringId="text_filter_notice"/></span>
                </div>}
            </div>
            <div className={classNames('button__stack', 'is-fluid')}>
                <Button isPrimary onClick={saveMessage}>
                    <Translation pageString="upsell" stringId="save_button_text"/>
                </Button>
            </div>
        </div>
    );
};

const GiftCard: React.FC<GiftCardProps> = ({ cartItemId, maxCharacters, text }) => {
    const translate = useTranslate();
    const { createTopLayerElement } = useContext(TopLayerContext);
    const [message, setMessage] = useState<string>(text);

    const dispatch = useTypedDispatch();

    const removeGiftcard = () => {
        setMessage('');
        dispatch(removeCartItemMessage({ id: cartItemId }));
    };

    const showSidebar = () => {
        createTopLayerElement(
            <SideBar key={`giftcard-${cartItemId}`} hasDesktopOverlay>
                <SideBarHeader headerText={translate('upsell', 'gift_card_title')}/>
                <SideBarBody>
                    <GiftCardForm
                        cartItemId={cartItemId}
                        maxCharacters={maxCharacters}
                        defaultValue={message}
                        onMessageChange={setMessage}
                    />
                </SideBarBody>
            </SideBar>,
            SideBar.ANIMATION_TIME,
        );
    };

    return <GiftCardEditor onOpenGiftCardEditor={showSidebar} handleRemoveGiftCard={removeGiftcard} cartItemId={cartItemId}/>;
};

export default GiftCard;
